import { useEffect, useRef } from "react";
import { Capacitor } from "@capacitor/core";
// Optional: Local notifications (requires @capacitor/local-notifications)
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import { LocalNotifications } from "@capacitor/local-notifications";
import { calculateDistance } from "@/lib/distance";
import { isOnline } from "@/lib/time";

export type ProximityUser = {
  id: string;
  name: string | null;
  lat: number | null;
  lng: number | null;
  location_updated_at: string | null;
};

type Options = {
  // meters threshold to trigger alert
  proximityMeters?: number;
  // cooldown per user before re-alerting (ms)
  cooldownMs?: number;
};

export default function useProximityAlerts(
  currentUserId: string | null,
  currentUserLocation: { lat: number; lng: number } | null,
  nearbyUsers: ProximityUser[],
  onPromptSendGift: (userId: string) => void,
  opts?: Options
) {
  const lastNotifiedRef = useRef<Map<string, number>>(new Map());
  const proximityMeters = opts?.proximityMeters ?? 20;
  const cooldownMs = opts?.cooldownMs ?? 10 * 60 * 1000; // 10 minutes

  // Ensure local notifications are set up (if plugin is available)
  useEffect(() => {
    try {
      if (!Capacitor.isNativePlatform()) return;
      if (!Capacitor.isPluginAvailable("LocalNotifications")) return;

      (async () => {
        try {
          const perm = await LocalNotifications.requestPermissions();
          // Register action types so the notification shows buttons
          await LocalNotifications.registerActionTypes({
            types: [
              {
                id: "SEND_GIFT_PROMPT",
                actions: [
                  { id: "SEND_GIFT", title: "Send Gift" },
                  { id: "DISMISS", title: "Dismiss", destructive: true },
                ],
              },
            ],
          });

          // Listen for action taps
          LocalNotifications.addListener(
            "localNotificationActionPerformed",
            (event: any) => {
              try {
                const actionId = event?.actionId;
                const userId = event?.notification?.extra?.userId as string | undefined;
                if (actionId === "SEND_GIFT" && userId) {
                  onPromptSendGift(userId);
                }
              } catch {}
            }
          );
        } catch (e) {
          console.warn("LocalNotifications setup failed:", e);
        }
      })();
    } catch {}
  }, [onPromptSendGift]);

  // Detect proximity and trigger alerts
  useEffect(() => {
    if (!currentUserId || !currentUserLocation) return;
    const now = Date.now();

    for (const u of nearbyUsers) {
      if (!u || !u.id || u.id === currentUserId) continue;
      if (!u.lat || !u.lng) continue;
      if (!isOnline(u.location_updated_at)) continue;

      const distanceKm = calculateDistance(
        currentUserLocation.lat,
        currentUserLocation.lng,
        u.lat,
        u.lng
      );
      const distanceMeters = distanceKm * 1000;
      if (distanceMeters >= proximityMeters) continue;

      const last = lastNotifiedRef.current.get(u.id) || 0;
      if (now - last < cooldownMs) continue;

      // Try native local notification; fallback to in-app prompt
      try {
        if (Capacitor.isNativePlatform() && Capacitor.isPluginAvailable("LocalNotifications")) {
          const id = Math.floor(now % 2147483647);
          LocalNotifications.schedule({
            notifications: [
              {
                id,
                title: "Nearby: Send a Gift?",
                body: `You're within ${Math.round(distanceMeters)}m of ${u.name ?? "someone"}.`,
                smallIcon: undefined,
                extra: { userId: u.id },
                actionTypeId: "SEND_GIFT_PROMPT",
              },
            ],
          }).catch(() => {
            onPromptSendGift(u.id);
          });
        } else {
          onPromptSendGift(u.id);
        }
      } catch {
        onPromptSendGift(u.id);
      }

      lastNotifiedRef.current.set(u.id, now);
    }
  }, [currentUserId, currentUserLocation?.lat, currentUserLocation?.lng, nearbyUsers, proximityMeters, cooldownMs, onPromptSendGift]);
}
